import { Component, Inject, OnInit, WritableSignal, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TransferFundResponseDto } from '../shared/models/transferFundResponseDto';
import { RequestsService } from '../shared/services/requests.service';
import { CommonService } from '../shared/services/common.service';
import { Matter } from '../shared/models/matter';
import { debounceTime, firstValueFrom, switchMap } from 'rxjs';
import { MatterService } from '../shared/services/matter.service';
import { MatOptionSelectionChange } from '@angular/material/core';
import { Participants, formatClientName } from '../shared/models/participants';
import { ApprovalDetails } from '../shared/models/ApprovalDetails';
import { SnackbarService } from '../shared/services/snackbar.service';
import { Bill } from '../shared/models/bill';
import { BillService } from '../shared/services/billService.service';
import { RequestType, RequestTypeOrder } from '../enums/request-type.enum';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { UserRequestsService } from '../shared/services/user-requests.service';
import { BankAccountService } from '../shared/services/bank-account.service';
import { BankAccount } from '../shared/models/bankAccount';
import { AuditService } from '../shared/services/audit.service';
import { buildTransferReason } from '../shared/utils/transfer-reason';

@Component({
  selector: 'xp-view-user-transfer',
  templateUrl: './view-user-transfer.component.html',
  styleUrl: './view-user-transfer.component.scss'
})
export class ViewUserTransferComponent implements OnInit {
    transferRequestForm!: FormGroup;
    matterDropdownData: Matter[] = [];
    isLoading: Boolean = false;
    noData: Boolean = false;
    matterNo: string = '';
    matterDesc: string = '';
    currentDate = new Date();
    bills: Bill[] = [];
    invoiceMap: { [key: string]: { totalAmount: number; balance: number } } = {};
    noBillsFoundMessage: string | null = null;
    currencySymbol: string | undefined;
    loading: boolean = false;
    displayedColumns: string[] = ['pay', 'warning', 'amount'];
    invoiceValues: { [key: string]: number } = {};
    showReturnReason: boolean = false;
    private invoicesEditable = false;
    private reasonEditedByUser = false;
    private reasonModeResolved = false;
    isDisabled = false;
    paymentMethodDropdown: string[] = ['Electronic funds transfer'];
    isRejectDisabled = false;
    isWithdrawDisabled = false;
    actionstepBaseUrl: string = '';
    actionstepMatterUrl: string = '';
    showLinkToOpenMatter: WritableSignal<boolean> = signal(false);
    filteredBankAccounts: BankAccount[] = [];
    actionstepMatterOverviewUrl: string = '';
    actionstepOverviewBaseUrl: string = '';
    bankAccounts: BankAccount[] = [];
    selectedBankAccountId: string = '';
    showDateTransaction: WritableSignal<boolean> = signal(false);

    constructor(
        private formBuilder: FormBuilder,
        private requestService: RequestsService,
        private matterService: MatterService,
        private commonService: CommonService,
        private snackbarService: SnackbarService,
        private dialogRef: MatDialogRef<ViewUserTransferComponent>,
        private billService: BillService,
        private dialog: MatDialog,
        private userRequestService: UserRequestsService,
        private bankAccountService: BankAccountService,
        private auditService: AuditService,
        @Inject(MAT_DIALOG_DATA) public transfer: TransferFundResponseDto
    ) {}

    get isCompleted(): boolean {
        return (this.transfer?.currentStage?.stageOrder ?? 0) >= 5;
    }

    get isTransactionDetailsVisible(): boolean {
        return (this.transfer?.currentStage?.stageOrder ?? 0) >= 5;
    }

    async ngOnInit() {
        this.initializeForm();
        this.setupAutocomplete();

        if (!this.isCompleted) {
            await this.getTransferFunds();
        }
        if (this.transfer) {
            this.patchFormValues(this.transfer);
            this.toggleFormFieldsByStage();
        }
        this.matterService.getMatterList('', '')
            .then((res) => {
                this.matterDropdownData = res;
            })
            .catch((error) => {
                this.commonService.apiSecurityError();
            });

        this.actionstepBaseUrl = await this.requestService.getActionstepUrl();
        this.actionstepOverviewBaseUrl = await this.requestService.getActionStepOverviewUrl();
        this.bankAccounts = await this.bankAccountService.getAllBankAccounts();
        await this.filterBankAccounts();

        this.transferRequestForm.get('clientBankAccount')!.valueChanges.subscribe((account: BankAccount | null) => {
            this.selectedBankAccountId = account?.accountId ?? '';
            if (this.matterNo) {
                this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}&ac_account_id=${this.selectedBankAccountId}`;
            }
        });

        if (this.transfer.clientBankAccountId) {
            this.transferRequestForm.patchValue({
                clientBankAccount: this.resolveBankAccount(this.transfer.clientBankAccountId) ?? null
            });
        }

        if (this.matterNo) {
            this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}&ac_account_id=${this.selectedBankAccountId}`;
            this.actionstepMatterOverviewUrl = `${this.actionstepOverviewBaseUrl}/${this.matterNo}`;
            this.showLinkToOpenMatter.set(true);
        }

        this.transferRequestForm.get('amount')?.disable();
    }

    initializeForm() {
        this.transferRequestForm = this.formBuilder.group({
            returnReason: [''],
            matterNumber: '',
            client: '',
            matter: '',
            matterName: '',
            reason: ['', Validators.required],
            invoice: '',
            amount: '',
            dateRequested: [null, Validators.required],
            requestedTime: [{ value: '' }],
            paymentMethod: [''],
            depositAccount: [''],
            transactionReference: [''],
            clientBankAccount: ['', Validators.required],
            dateTransaction: '',
            transactionTime:''
        });
    }

    patchFormValues(data: TransferFundResponseDto) {
        this.transferRequestForm.patchValue({
            returnReason: data.returnReason,
            matterNumber: data.matterNo,
            matter: data.matter,
            matterName: data.matterNo + " - " + data.matter,
            client: data.client,
            reason: data.reason,
            invoice: data.invoice,
            amount: data.amount,
            dateRequested: data.dateRequested ? new Date(data.dateRequested) : null,
            paymentMethod: data.paymentMethod,
            depositAccount: data.depositAccount,
            transactionReference: data.transactionReference,
            dateTransaction: data.dateTransaction
        });

        if (data.dateTransaction) {
            const d = new Date(data.dateTransaction);
            this.transferRequestForm.get('transactionTime')?.setValue(this.toTimeString(d));
            this.showDateTransaction.set(true);
        }
        else {
            this.showDateTransaction.set(false);
        }

        if (data.invoice) {
            this.invoiceValues = this.parseInvoiceJson(data.invoice);
        }

        if (data.matterNo) {
            this.matterNo = data.matterNo.toString();
            this.onMatterChange();
        }
        if (data.dateRequested) {
            const d = new Date(data.dateRequested);
            this.transferRequestForm.get('requestedTime')?.setValue(this.toTimeString(d));
        }

    }

    private toggleFormFieldsByStage() {
        const isParalegalStage = this.transfer.currentStage?.stageOrder === 2;
        const isPartnerStage = (this.transfer.currentStage?.stageOrder === 3 && this.transfer.isCreatorPartnerApprove) ?? false;

        this.isRejectDisabled = this.transfer.currentStage?.stageOrder === 1 || (this.transfer.currentStage?.stageOrder ?? 0) >= 5;
        this.isWithdrawDisabled = this.transfer.currentStage?.stageOrder === 1 || this.transfer.currentStage?.stageOrder === 7;

        this.isDisabled = !(isParalegalStage || isPartnerStage);
        const isEditable = isParalegalStage || isPartnerStage;

        Object.keys(this.transferRequestForm.controls).forEach(controlName => {
            const control = this.transferRequestForm.get(controlName);

            if (!control) return;
            if (controlName === 'matter') {
                control.disable();
                return;
            }
            if (isEditable) {
                control.enable();
            } else {
                control.disable();
            }
        });

        this.showReturnReason = isEditable;
        this.transferRequestForm.get('dateRequested')?.disable();
        this.transferRequestForm.get('requestedTime')?.disable();
    }


    private parseInvoiceJson(invoiceString: string): { [key: string]: number } {
        try {
            const parsed = JSON.parse(invoiceString);
            const result: { [key: string]: number } = {};

            Object.entries(parsed).forEach(([key, value]) => {
                const match = key.match(/Invoice #(\d+)/);
                if (match) {
                    result[match[1]] = parseFloat(value as string);
                }
            });

            return result;
        } catch (e) {
            console.error('Invalid invoice JSON:', e);
            return {};
        }
    }

    private resolveBankAccount(accountId?: string): BankAccount | undefined {
        return this.bankAccounts.find(a => a.accountId === accountId);
    }

    private toTimeString(date: Date): string {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    closeDialog() {
        this.dialogRef.close(true);
    }

    displayMatter(matter: Matter) {
        return matter ? `${matter.number} - ${matter.description}` : '';
    }

    validateInput(event: KeyboardEvent): void {
        const allowedKeys = /[0-9.,]/; // Only allow numbers, dot, and comma
        const key = event.key;
        // Allow control keys like Backspace, Arrow keys, Delete, Tab, etc.
        if (['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'].includes(key)) {
            return;
        }
        // Prevent invalid input
        if (!allowedKeys.test(key)) {
            event.preventDefault();
        }
    }

    getClientDetails(matterNumber: number) {
        this.matterService.getMatterClient(Number(this.matterNo), 27)
            .then((res: Participants[]) => {

                if (!res || res.length === 0) {
                    this.transferRequestForm.get('client')?.setValue('No client found');
                    return;
                }
                const displayClient = res.map(formatClientName).join(', ');

                this.transferRequestForm.get('client')?.setValue(displayClient);
            })
            .catch((error) => {
                this.transferRequestForm.get('client')?.setValue('No client found');
            });
    }

    onMatterChange() {
        if (this.isCompleted) {
            this.loadInvoiceFromStoredData();
            return;
        }
        if (this.matterNo) {
            this.billService.getBillsByMatter(this.matterNo).subscribe({
                next: (data) => {
                    this.bills = data;
                    this.noBillsFoundMessage = null;

                    this.currencySymbol = data.length > 0
                        ? new Intl.NumberFormat('en', {
                            style: 'currency',
                            currency: data[0].currency
                        })
                            .formatToParts(0)
                            .find(part => part.type === 'currency')?.value || data[0].currency
                        : '';

                    const isParalegalStage = this.transfer.currentStage?.stageOrder === 2;
                    const isPartnerStage = (this.transfer.currentStage?.stageOrder === 3 && this.transfer.isCreatorPartnerApprove) ?? false;
                    const isEditable = isParalegalStage || isPartnerStage;

                    this.bills.forEach(bill => {
                        const controlName = this.getControlName(bill);
                        const maxBalance = parseFloat(bill.totalDue);

                        const control = this.formBuilder.control(0, [Validators.max(maxBalance)]);
                        this.transferRequestForm.setControl(controlName, control);

                        const invoiceAmount = this.invoiceValues[bill.ourReference];
                        if (invoiceAmount !== undefined) {
                            control.setValue(invoiceAmount, { emitEvent: false });
                        }

                        if (!isEditable) {
                            control.disable();
                        }

                        control.valueChanges.subscribe(() => {
                             this.updateTotalAmount();
                             this.updateAutoReason();
                        });
                    });

                    this.updateTotalAmount();

                    this.invoicesEditable = isEditable;
                    if (!this.reasonModeResolved) {
                        this.reasonModeResolved = true;
                        this.restoreReasonMode();
                    } else {
                        this.updateAutoReason();
                    }
                },
                error: (err) => {
                    this.bills = [];
                    if (err.status === 404) {
                        this.noBillsFoundMessage = `No invoices available for the selected matter.`;
                    } else {
                        this.noBillsFoundMessage = `Failed to load invoices. Please try again later.`;
                        console.error('Error loading bills:', err);
                    }
                }
            });
        }
    }

    onReasonInput(): void {
        this.reasonEditedByUser = true;
    }

    private buildAutoReason(): string {
        return buildTransferReason(this.bills
            .filter(bill => parseFloat(this.transferRequestForm.get(this.getControlName(bill))?.value) > 0)
            .map(bill => bill.ourReference));
    }

    private updateAutoReason(): void {
        if (!this.invoicesEditable || this.reasonEditedByUser) return;

        this.transferRequestForm.get('reason')?.setValue(this.buildAutoReason(), { emitEvent: false });
    }

    private restoreReasonMode(): void {
        if (!this.invoicesEditable) return;

        const savedReason = this.transferRequestForm.get('reason')?.value ?? '';
        this.reasonEditedByUser = savedReason !== '' && savedReason !== this.buildAutoReason();
        this.updateAutoReason();
    }

    private loadInvoiceFromStoredData(): void {
        if (!this.transfer?.invoice) return;

        let invoiceObj: { [key: string]: string };
        try {
            invoiceObj = JSON.parse(this.transfer.invoice);
        } catch {
            this.noBillsFoundMessage = 'Invoice data could not be read.';
            return;
        }

        this.bills = [];
        this.invoiceMap = {};
        this.noBillsFoundMessage = null;
        let index = 1;

        Object.entries(invoiceObj).forEach(([key, value]) => {
            const invoiceNumberMatch = key.match(/Invoice #(\d+)/);
            if (!invoiceNumberMatch) return;

            const ourReference = invoiceNumberMatch[1];

            const balanceMatch = key.match(/Balance Due:\s*([^\d\s]*)([\d.,]+)/);
            const balance = balanceMatch ? parseFloat(balanceMatch[2].replace(',', '')) : 0;

            if (!this.currencySymbol && balanceMatch?.[1]) {
                this.currencySymbol = balanceMatch[1];
            }

            const amount = parseFloat(value) || 0;

            const bill: Bill = {
                id: index++,
                ourReference,
                totalDue: balance.toFixed(2),
                currency: this.transfer.currencyCode ?? '',
                action: ''
            };
            this.bills.push(bill);

            this.invoiceMap[ourReference] = { totalAmount: amount, balance };

            const control = this.formBuilder.control({ value: amount, disabled: true });
            this.transferRequestForm.setControl(this.getControlName(bill), control);
        });

        this.updateTotalAmount();
    }


    updateTotalAmount() {
        let total = 0;
        this.bills.forEach(bill => {
            const control = this.transferRequestForm.get(this.getControlName(bill));
            if (control) {
                total += parseFloat(control.value) || 0;
            }
        });
        this.transferRequestForm.get('amount')?.setValue(total, { emitEvent: false });
    }


    private setupAutocomplete() {
        this.transferRequestForm.controls['matter'].valueChanges.pipe(
            debounceTime(300),
            switchMap((query) => {
                let id: string = '';
                let name: string = '';

                if (!isNaN(Number(query))) {
                    id = query;
                    name = '';
                }
                else {
                    id = '';
                    name = query;
                }
                if (id == null) {
                    id = ''
                }
                this.isLoading = true;
                this.noData = true;
                return this.matterService.getMatterList(id, name);
            })
        ).subscribe({
            next: (data) => {
                this.noData = false;
                if (data == null) {
                    this.noData = true;
                }
                this.matterDropdownData = data;
                this.isLoading = false;
            },
            error: (error) => {
                this.noData = true;
                if (error.status == 401) {
                    this.commonService.apiSecurityError();
                }
            },
            complete: () => {
                this.isLoading = false;
            }
        })
    }

    getInvoiceData(ourReference: string | number) {
        return this.invoiceMap[ourReference] || null;
    }

    getControlName(bill: Bill) {
        return `invoice${bill.id}`
    }

    onUrlCopied() {
        this.snackbarService.successSnackBar("URL copied to clipboard");
    }

    filterBankAccounts() {
        this.filteredBankAccounts = [...this.bankAccounts];
    }

    async getTransferFunds() {
            const response: TransferFundResponseDto[] =
                await this.requestService.getTransferFundsList(true);

            this.invoiceMap = {};

            response.forEach((item: TransferFundResponseDto) => {
                if (!item.invoice) return;
                const invoiceObj = JSON.parse(item.invoice);

                Object.keys(invoiceObj).forEach((invoiceKey: string) => {

                    const balanceText = invoiceKey.match(/Balance Due: .*?([\d.,]+)/);
                    const balance = balanceText ? parseFloat(balanceText[1]) : 0;

                    const invoiceNumberMatch = invoiceKey.match(/Invoice #(\d+)/);
                    const invoiceNumber = invoiceNumberMatch
                        ? invoiceNumberMatch[1]
                        : invoiceKey;

                    const invoiceAmount = parseFloat(invoiceObj[invoiceKey]) || 0;

                    if (!this.invoiceMap[invoiceNumber]) {
                        this.invoiceMap[invoiceNumber] = {
                            totalAmount: 0,
                            balance
                        };
                    }

                    this.invoiceMap[invoiceNumber].totalAmount += invoiceAmount;
                });
            });
    }

    delete() {
        var configOk = {
            title: 'Are you sure?',
            message: 'The data associated with the request will be deleted.',
            dialogType: 'warning',
            buttonType: 'ConfirmCancel',
            confirmText: 'Confirm',
            cancelText: 'Cancel',
        }
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: configOk,
            width: '30%',
        });

        dialogRef.afterClosed().subscribe(res => {
            if (res) {
                this.requestService.deleteTransferFund(this.transfer.id)
                    .then((res) => {
                        if (res) {
                            this.snackbarService.successSnackBar(`${res}`);
                            this.dialogRef.close(true);
                        }
                    })
                    .catch((error) => {
                        this.snackbarService.errorSnackBar("Failed to delete transfer fund request. Please try again.");
                    });
            }
        })
    }

    async approveRequest() {
        if (this.transferRequestForm.invalid) {
            this.transferRequestForm.markAllAsTouched();
            return;
        }
        let invoices: { [key: string]: string } = {};
        this.bills.forEach(bill => {
            const amountControl = this.transferRequestForm.get(`invoice${bill.id}`);
            if (amountControl && amountControl.value > 0) {
                invoices[`Invoice #${bill.ourReference} (Balance Due: ${this.currencySymbol}${bill.totalDue})`] = amountControl.value;
            }
        });

        const isPartnerStage = (this.transfer.currentStage?.stageOrder === 3 && this.transfer.isCreatorPartnerApprove) ?? false;
        const selectedAccount = this.resolveBankAccount(this.selectedBankAccountId);
        const approvalDetails: ApprovalDetails = {
            client: this.transferRequestForm.value.client,
            reasonForTransfer: this.transferRequestForm.value.reason,
            requestDate: this.transferRequestForm.value.dateRequested,
            invoice: JSON.stringify(invoices),
            amount: this.transferRequestForm.get('amount')?.value,
            paymentMethod: this.transferRequestForm.get('paymentMethod')?.value,
            depositAccount: this.transferRequestForm.get('depositAccount')?.value,
            transactionReference: this.transferRequestForm.get('transactionReference')?.value,
            clientBankAccountId: this.selectedBankAccountId || this.transfer.clientBankAccountId,
            clientBankAccountName: selectedAccount
                ? `${selectedAccount.accountId} - ${selectedAccount.description}`
                : this.transfer.clientBankAccountName,
        };

        if (isPartnerStage) {
            approvalDetails.fundsCheckedByPartner = true;
        }

        this.requestService.approveRequest(
            this.transfer.id,
            RequestType.TransferFund,
            (this.transfer.currentStage!.id) + 1,
            approvalDetails
        )
            .then((res: string) => {
                if (res) {
                    this.snackbarService.successSnackBar(`${res}`);
                    this.dialogRef.close(true);
                }
            })
            .catch((error) => {
                this.snackbarService.errorSnackBar("Failed to approve payment request. Please try again.");
            });
    }

    async withdrawTransfer() {
        const audits = await this.auditService.getUserAuditByRequest(this.transfer.id, RequestTypeOrder[RequestType.TransferFund]);

        const latestAudit = audits?.length ? audits[audits.length - 1] : null;
        const rpaStatus = latestAudit?.wasRpaSuccessful ?? null;
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '600px',
            data: {
                title: 'Withdraw Transfer Fund Request?',
                message: 'Are you sure you want to withdraw this transfer tund request?',
                dialogType: 'warning',
                buttonType: 'ConfirmCancel',
                confirmText: 'Confirm',
                cancelText: 'Cancel',
                matterNo: this.transfer?.matterNo,
                currentStageId: this.transfer?.currentStage?.stageOrder,
                rpaStatus: rpaStatus,
                skipAccountsValidation: true,
                selectedBankAccountId: this.selectedBankAccountId,
            }
        });
        dialogRef.afterClosed().subscribe(res => {
            if (res) {
                this.userRequestService.withdrawTransferRequest(this.transfer.id)
                    .then((res) => {
                        if (res) {
                            this.snackbarService.successSnackBar(`${res}`);
                            this.dialogRef.close(true);
                        }
                    })
                    .catch((error) => {
                        this.snackbarService.errorSnackBar("Failed to withdraw transfer request. Please try again.");
                    });
            }
        })
    }
}
